import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Upload, 
  Download, 
  FileSpreadsheet,
  FileJson,
  CheckCircle, 
  AlertCircle,
  Info,
  Loader2,
  X
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';



const ExcelUpload = () => {
  const { token, selectedPlatform , backendUrl} = useAuth();
  
  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;
  const { toast } = useToast();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingSample, setDownloadingSample] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    // Validate file type - accept Excel and JSON
    const validExtensions = ['.xlsx', '.xls', '.json'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid File Type",
        description: "Please select an Excel file (.xlsx, .xls) or JSON file (.json)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select a file smaller than 10MB",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const downloadTemplate = async () => {
    if (!token) return;
    
    try {
      setDownloadingTemplate(true);
      
      const response = await axios.get(`${API}/transactions/excel/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'transaction_template.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Template Downloaded",
        description: "Excel template has been downloaded successfully",
        variant: "default",
      });
      
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download Excel template",
        variant: "destructive",
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const downloadSample = async () => {
    if (!token) return;
    
    try {
      setDownloadingSample(true);
      
      const response = await axios.get(`${API}/transactions/excel/sample`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 50,
          platform_id: selectedPlatform
        },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'sample_transactions.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sample Downloaded",
        description: "Sample transactions Excel file has been downloaded",
        variant: "default",
      });
      
    } catch (error) {
      console.error('Error downloading sample:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download sample transactions",
        variant: "destructive",
      });
    } finally {
      setDownloadingSample(false);
    }
  };

  const downloadJsonTemplate = () => {
    // Create sample JSON template using database field names (matches export format)
    // Standardized format - exported JSON can be directly re-imported
    const template = [
      {
        "id": "",
        "transaction_id": "",
        "transaction_date": "2026-02-14T10:30:00",
        "customer_name": "John Doe",
        "customer_email": "john.doe@example.com",
        "customer_country": "US",
        "amount": 100.00,
        "currency": "USD",
        "converted_amount": 100.00,
        "converted_currency": "USD",
        "payment_method": "visa",
        "card_masked": "4111********1111",
        "card_brand": "Visa",
        "card_country": "US",
        "transaction_type": "sale",
        "channel": "Web",
        "bank_reference": "",
        "bank_approval_code": "",
        "approval_code": "",
        "platform_id": "",
        "processor": "stripe",
        "merchant_name": "Example Merchant",
        "ip": "192.168.1.1",
        "ip_country": "US",
        "risk_action": "approved",
        "risk_score": 10,
        "reference": "REF-001",
        "description": "Sample transaction",
        "status": "completed",
        "result_code": "00-Approved",
        "country": "US"
      }
    ];
    
    const jsonContent = JSON.stringify(template, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transaction_template_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Template Downloaded",
      description: "JSON template has been downloaded. Fill in your data and upload.",
      variant: "default",
    });
  };

  const uploadFile = async () => {
    if (!selectedFile || !token) return;
    
    try {
      setUploading(true);
      
      const isJsonFile = selectedFile.name.toLowerCase().endsWith('.json');
      
      if (isJsonFile) {
        // Handle JSON upload
        const fileContent = await selectedFile.text();
        let jsonData;
        
        try {
          jsonData = JSON.parse(fileContent);
        } catch (parseError) {
          toast({
            title: "Invalid JSON",
            description: "The file contains invalid JSON format",
            variant: "destructive",
          });
          setUploading(false);
          return;
        }
        
        // Ensure it's an array
        if (!Array.isArray(jsonData)) {
          jsonData = [jsonData];
        }
        
        const response = await axios.post(`${API}/transactions/json/upload`, {
          transactions: jsonData,
          platform_id: selectedPlatform
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 900000
        });
        
        setUploadResult(response.data);
        
        if (response.data.results.processed > 0) {
          toast({
            title: "Upload Successful",
            description: `Successfully processed ${response.data.results.processed} transactions from JSON`,
            variant: "default",
          });
        }
      } else {
        // Handle Excel upload
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const response = await axios.post(`${API}/transactions/excel/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          params: {
            platform_id: selectedPlatform
          },
          timeout: 900000 // 15 minutes timeout for large file uploads
        });
        
        setUploadResult(response.data);
        
        if (response.data.results.processed > 0) {
          toast({
            title: "Upload Successful",
            description: `Successfully processed ${response.data.results.processed} transactions`,
            variant: "default",
          });
        }
      }
      
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to upload file';
      
      toast({
        title: "Upload Failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      setUploadResult({
        results: {
          total_rows: 0,
          processed: 0,
          errors: [errorMessage],
          warnings: [],
          created_transactions: []
        }
      });
      
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setUploadResult(null);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
            <Upload className="w-8 h-8 text-teal-500" />
            Bulk Transaction Upload
          </h1>
          <p className="text-muted-foreground mt-2">
            Upload transaction data in bulk using Excel or JSON files
          </p>
        </div>
      </div>

      {/* Download Templates Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Excel Template
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Download Excel template with headers and validation instructions.
            </p>
            <Button onClick={downloadTemplate} disabled={downloadingTemplate} className="w-full">
              {downloadingTemplate ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download Excel Template
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5" />
              JSON Template
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Download JSON template matching the export format structure.
            </p>
            <Button onClick={downloadJsonTemplate} variant="outline" className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Download JSON Template
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Sample Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Download existing transactions to see the correct format.
            </p>
            <Button onClick={downloadSample} disabled={downloadingSample} variant="outline" className="w-full">
              {downloadingSample ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Download Sample (50 rows)
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFile}
                    className="ml-4"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex gap-2 justify-center">
                  <Button onClick={uploadFile} disabled={uploading}>
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload File
                      </>
                    )}
                  </Button>
                  
                  <Button variant="outline" onClick={clearFile} disabled={uploading}>
                    Choose Different File
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="mx-auto w-12 h-12 text-muted-foreground">
                  <Upload className="w-full h-full" />
                </div>
                <div>
                  <p className="text-lg font-medium">Drop your Excel or JSON file here</p>
                  <p className="text-muted-foreground">or click to browse</p>
                </div>
                <div>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.json"
                    onChange={handleFileInputChange}
                    className="hidden"
                    id="file-input"
                  />
                  <label htmlFor="file-input">
                    <Button variant="outline" asChild>
                      <span>Choose File</span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Supports .xlsx, .xls, and .json files up to 10MB
                </p>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Processing...</span>
              </div>
              <Progress value={undefined} className="w-full" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Results */}
      {uploadResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {uploadResult.results.processed > 0 ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              Upload Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold">{uploadResult.results.total_rows}</div>
                <div className="text-sm text-muted-foreground">Total Rows</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{uploadResult.results.processed}</div>
                <div className="text-sm text-muted-foreground">Processed</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{uploadResult.results.errors.length}</div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{uploadResult.results.warnings.length}</div>
                <div className="text-sm text-muted-foreground">Warnings</div>
              </div>
            </div>

            {/* Errors */}
            {uploadResult.results.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium mb-2">Errors encountered:</div>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {uploadResult.results.errors.slice(0, 10).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {uploadResult.results.errors.length > 10 && (
                      <li className="text-muted-foreground">
                        ... and {uploadResult.results.errors.length - 10} more errors
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Warnings */}
            {uploadResult.results.warnings.length > 0 && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium mb-2">Warnings:</div>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {uploadResult.results.warnings.slice(0, 5).map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                    {uploadResult.results.warnings.length > 5 && (
                      <li className="text-muted-foreground">
                        ... and {uploadResult.results.warnings.length - 5} more warnings
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Success Message */}
            {uploadResult.results.processed > 0 && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Successfully imported {uploadResult.results.processed} transactions. 
                  You can now view them in the transaction history.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Upload Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Before You Upload:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Download the JSON template to see the required format</li>
                <li>Exported JSON files can be directly re-imported</li>
                <li>Ensure all required fields are filled: customer_name, customer_email, amount</li>
                <li>Optional: currency, payment_method, status, result_code, etc.</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Supported Fields:</h4>
              <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                <div>
                  <Badge variant="outline" className="mr-2">Required</Badge>
                  customer_name, customer_email, amount
                </div>
                <div>
                  <Badge variant="secondary" className="mr-2">Optional</Badge>
                  currency, payment_method, card_masked, card_brand, status, result_code, merchant_name, processor, etc.
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExcelUpload;